import React, { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import UserDashboard from "../../components/dashboard/user/UserDashboard";
import AppContext from "../../context/AppContext";
import Cookies from "js-cookie";
import { fetchDynamicWorkflows } from "../../service/UserDashboard.js";
import { getPermittedActivities } from "../../utils/activityPermissions";
import Joyride from "react-joyride";
import translations from "../../components/common/Translations.js";
import { toast } from "../../utils/toast";
import PowerBIDashboard from "../../components/dashboard/powerbi/PowerBiDashboard.jsx";
import InventoryDashboard from "../../components/dashboard/inventory/InventoryDashboard.jsx";
import { components } from 'react-select';
import ReactSelector from "../../components/common/CommonReactSelector.js";
import { Button } from "../../components/common/Button.js";
import { canViewInventoryManagement, hasScreenAccess } from "../../utils/permissions";
import { Activity, BarChart3, Boxes, User } from "lucide-react";
import { Spinner } from "../../Spinner.js";

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};
const DASHBOARDS = {
  USER: "user",
  ACTIVITY: "activity",
  POWERBI: "powerbi",
  INVENTORY: "inventory",
};

const Dashboard = () => {
  const navigate = useNavigate();

  const GoExport = () => {
    Cookies.set("viewMode", "dashboard");
    navigate("/pdf-generator");
  };

  const { theme, langMode, selectedOrgId, runTour, dashboard, userType, updateProjectId, projectId, setHeaderActivitySelect } =
    useContext(AppContext);
  console.log(langMode)
  const [activeDashboard, setActiveDashboard] = useState(DASHBOARDS.USER);
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState("");
  const [selectedWorkflowName, setSelectedWorkflowName] = useState("");
  const [open, setOpen] = useState(false);
  const hasActivityDashboardAccess = hasScreenAccess("ACTIVITY_DASHBOARD", "canView");
  // Shown only to users with View access to the Inventory Management screen.
  const hasInventoryDashboardAccess = canViewInventoryManagement();

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));

  useEffect(() => {
    const { organization, departmentList, projectList } = userDetails;
    const deparmentsList = JSON.parse(localStorage.getItem("departmentId"))
    const selectedOrgsId = JSON.parse(localStorage.getItem("selectedOrgId"))
    const projectsId = ((localStorage.getItem("projectId") || "").replace(/"/g, ""))

    if (deparmentsList || selectedOrgsId || projectsId) {
      localStorage.setItem("selectedOrgId", JSON.stringify(selectedOrgsId || null));
    } else {
      if (userDetails) {
        localStorage.setItem("selectedOrgId", JSON.stringify(organization?.organizationId || null));
      }
    }

  }, [userDetails]);

  useEffect(() => {
    const justLoggedIn = localStorage.getItem("justLoggedIn");

    if (justLoggedIn === "true") {
      setOpen(true);
      localStorage.removeItem("justLoggedIn");
    }
  }, []);

  useEffect(() => {
    if (workflows.length > 0) {
      const defaultWorkflow = workflows[0];
      setSelectedWorkflowName(defaultWorkflow.workflowName);
      setSelectedWorkflowId(defaultWorkflow.workflowId);
    } else {
      setSelectedWorkflowName("");
      setSelectedWorkflowId("");
    }
  }, [workflows, selectedOrgId]);

  const handleWorkflowChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedWorkflowName(selectedOption.label);
      setSelectedWorkflowId(selectedOption.value);
    }
  };

  const switchDashboard = (dashboard) => {
    setLoading(true);
    setTimeout(() => {
      setActiveDashboard(dashboard);
      setLoading(false);
    }, 300);
  };

  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        const response = await fetchDynamicWorkflows(projectId);
        if (
          response?.body?.value &&
          Array.isArray(response.body.value) &&
          response.body.value.length === 0
        ) {
          toast.error(translations[langMode].noworkflowinorg);
        }
        setWorkflows(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Error loading workflows:", error);
        setWorkflows([]);
      }
    };
    loadWorkflows();
  }, [projectId]);

  const goactivtydashbord = () => {
    navigate("/activty-dashboard");
  };

  const permittedWorkflows = useMemo(() => {
    return getPermittedActivities(workflows);
  }, [workflows]);

  const Options = useMemo(() => {
    return Array.isArray(permittedWorkflows)
      ? permittedWorkflows.map((workflow) => ({
        value: workflow.workflowId,
        label: workflow.workflowName,
      }))
      : [];
  }, [permittedWorkflows]);

  // Keep selectedWorkflowId valid if options change
  useEffect(() => {
    if (Options.length > 0) {
      const isValid = Options.some((w) => w.value === selectedWorkflowId);
      if (!isValid) {
        setSelectedWorkflowId(Options[0].value);
      }
    }
  }, [Options, selectedWorkflowId]);

  // Render the workflow select into the top header bar instead of this page,
  // and clear it on unmount so it doesn't leak into pages without this selector.
  useEffect(() => {
    if (!setHeaderActivitySelect) return undefined;

    // The workflow picker only drives the User dashboard.
    if (activeDashboard === DASHBOARDS.POWERBI || activeDashboard === DASHBOARDS.INVENTORY || Options.length === 0) {
      setHeaderActivitySelect(null);
      return undefined;
    }

    const currentOption =
      Options.find((w) => w.value === selectedWorkflowId) || Options[0] || null;

    setHeaderActivitySelect(
      <div className="chooseworkflow w-full min-w-0 max-w-full">
        <ReactSelector
          defaultValue={currentOption}
          options={Options}
          components={Placeholder}
          placeholder={translations[langMode].select}
          onChange={handleWorkflowChange}
          height="42px"
          menuPortalTarget={document.body}
        />
      </div>
    );

    return () => setHeaderActivitySelect(null);
  }, [
    setHeaderActivitySelect,
    activeDashboard,
    Options,
    selectedWorkflowId,
    langMode,
  ]);

  return (
    <div className="w-full">
      <Joyride
        steps={dashboard}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        styles={{
          options: {
            arrowColor: "#fff",
            zIndex: 10000,
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#4C3BCF",
            textColor: "#333",
          },
          tooltip: {
            borderRadius: "10px",
          },
          buttonSkip: {
            color: "#4C3BCF",
          },
          buttonNext: {
            backgroundColor: "#4C3BCF",
          },
          buttonBack: {
            color: "#4C3BCF",
          },
        }}
      />
      <div
        className={`w-full ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          } `}
      >
        <h4
          className={`page-title mb-3 sm:mb-4 inline-flex items-center gap-2 text-base sm:text-lg font-bold ${theme === "dark"
            ? "dark-mode "
            : theme === "high-contrast"
              ? "high-contrast  "
              : ""
            }`}
        >
          {translations[langMode].dashboardManagement}
        </h4>
        <div className="flex w-full flex-col gap-4 sm:gap-6">
        <div className="flex items-center justify-between gap-3 sm:gap-4 w-full">
          <div className="w-full sm:w-auto">
            <div
              className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-1.5 w-full rounded-xl bg-white p-1.5 shadow-sm dark:bg-gray-800 sm:flex sm:items-center sm:w-auto sm:gap-2"
              role="group"
            >
              <Button
                onClick={() => switchDashboard(DASHBOARDS.USER)}
                isActive={activeDashboard === DASHBOARDS.USER}
                aria-label="User Dashboard"
                className={`h-9 min-h-[36px] sm:h-10 sm:min-h-[40px] rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 sm:gap-2 transition-all ${
                  hasActivityDashboardAccess ? "min-[380px]:col-span-2 sm:col-span-1" : "col-span-1"
                }`}
              >
                <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                <span>{translations[langMode].userdashboard}</span>
              </Button>

              {hasActivityDashboardAccess && (
                <Button
                  onClick={goactivtydashbord}
                  isActive={activeDashboard === DASHBOARDS.ACTIVITY}
                  aria-label="Activity Dashboard"
                  className="h-9 min-h-[36px] sm:h-10 sm:min-h-[40px] rounded-lg px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold col-span-1 flex items-center justify-center gap-1.5 sm:gap-2 transition-all text-center"
                >
                  <Activity className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                  <span className="truncate">{translations[langMode].activitydashboard}</span>
                </Button>
              )}

              <Button
                onClick={() => switchDashboard(DASHBOARDS.POWERBI)}
                isActive={activeDashboard === DASHBOARDS.POWERBI}
                aria-label="PowerBi Dashboard"
                className="h-9 min-h-[36px] sm:h-10 sm:min-h-[40px] rounded-lg px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold col-span-1 flex items-center justify-center gap-1.5 sm:gap-2 transition-all text-center"
              >
                <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                <span className="truncate">{translations[langMode].powerbidashboard}</span>
              </Button>

              {hasInventoryDashboardAccess && (
                <Button
                  onClick={() => switchDashboard(DASHBOARDS.INVENTORY)}
                  isActive={activeDashboard === DASHBOARDS.INVENTORY}
                  aria-label="Inventory Dashboard"
                  data-testid="inventory-dashboard-tab"
                  className="h-9 min-h-[36px] sm:h-10 sm:min-h-[40px] rounded-lg px-2.5 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold col-span-1 flex items-center justify-center gap-1.5 sm:gap-2 transition-all text-center"
                >
                  <Boxes className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
                  <span className="truncate">{translations[langMode].inventoryDashboard}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
        <div
          className={`${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            } flex w-full`}
        >
          {loading ? (
            <Spinner />
          ) : activeDashboard === DASHBOARDS.USER ? (
            <UserDashboard
              selectedWorkflowId={selectedWorkflowId}
              selectedOrgId={selectedOrgId}
            />
          ) : activeDashboard === DASHBOARDS.POWERBI ? (
            <PowerBIDashboard />
          ) : activeDashboard === DASHBOARDS.INVENTORY ? (
            <InventoryDashboard />
          ) : null}
        </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
